import { COMPANY } from '@/lib/crm/core/entityTypes';
import { formatPipelineLabel } from '@/lib/crm/pipeline/utils/formatPipelineLabel';
import { getFaviconUrl } from '@/lib/crm/utils/avatar';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { EntityBadgeData } from './types';

/** Pixel size requested from the Google favicon service. Plenty for any
 *  badge variant (max icon size ~16-18px) and matches the common favicon
 *  resolution served by most domains. */
const COMPANY_FAVICON_SIZE = 64;

/**
 * Stage entity types that get a pipeline-name prefix on their display
 * label (e.g. "Sales Pipeline: Negotiation" instead of just
 * "Negotiation"). Mirrors the legacy `STAGE_TIMELINE_TYPES` map from
 * `CrmObjectBadge.tsx` so the consolidated badge primitives keep the
 * same labelling behaviour the deal pipeline UI depends on.
 */
const STAGE_ENTITY_TYPES = new Set<string>([
  'ahoy_deal_pipeline_stage',
  'ahoy_contact_lifecycle_stage',
]);

/**
 * Stage types where the pipeline-name prefix is *redundant* and should
 * be skipped. Lifecycle stages have a single pipeline and the column
 * header / surface label already conveys the context, so prepending
 * "Contact Lifecycle: " just adds noise (see #5189).
 */
const STAGES_WITHOUT_PIPELINE_PREFIX = new Set<string>([
  'ahoy_contact_lifecycle_stage',
]);

export function getAvatarUrl(object: CrmObject): string | undefined {
  const profilePhoto = getPropertyValue<string>(
    object.properties?.ahoy_profile_photo_url
  );
  if (profilePhoto) {
    return profilePhoto;
  }
  const avatarUrl = getPropertyValue<string>(
    object.properties?.ahoy_avatar_url
  );
  if (avatarUrl) {
    return avatarUrl;
  }
  // Companies don't have profile photos — their identity-image lives in
  // their domain. Resolve via the Google favicon service so company badges
  // show recognizable brand logos in cells / lists / hover previews instead
  // of falling back to a generic building icon. Same mechanism the
  // standalone `CrmObjectAvatar` already uses.
  if (object.entity_type_name === COMPANY) {
    const domain = getPropertyValue<string>(object.properties?.ahoy_domain);
    if (domain) {
      return getFaviconUrl(domain, COMPANY_FAVICON_SIZE);
    }
  }
  return undefined;
}

/**
 * Apply the pipeline-name prefix to a stage entity's display label.
 *
 * For stage entities (currently deal pipeline stages), prepends the
 * formatted pipeline name so cross-pipeline lists can disambiguate
 * stages that share a name. Lifecycle stages are explicitly skipped —
 * see `STAGES_WITHOUT_PIPELINE_PREFIX` above.
 *
 * Returns the input label unchanged for non-stage entities or when the
 * pipeline property is missing.
 */
export function applyStagePipelinePrefix(
  object: CrmObject,
  displayName: string
): string {
  if (!STAGE_ENTITY_TYPES.has(object.entity_type_name)) {
    return displayName;
  }
  if (STAGES_WITHOUT_PIPELINE_PREFIX.has(object.entity_type_name)) {
    return displayName;
  }
  const pipelineName = getPropertyValue<string>(
    object.properties?.ahoy_pipeline
  );
  if (!pipelineName) {
    return displayName;
  }
  return `${formatPipelineLabel(pipelineName)}: ${displayName}`;
}

export function buildEntityBadgeData(object: CrmObject): EntityBadgeData {
  return {
    id: object.entity_id,
    entityType: object.entity_type_name,
    displayName: applyStagePipelinePrefix(object, getDisplayName(object)),
    avatarUrl: getAvatarUrl(object),
  };
}
